import { QUERY_PARAMS } from "@av/apps/recruitment/ats-common/routes/jobs/query-params";
import {
  AllLocationTypes,
  AllProvinces,
  type JobPostFilters,
  type LocationType,
  type Province,
} from "@av/apps/recruitment/ats-common/types";

const JOB_BOARD_QUERY_PARAMS = QUERY_PARAMS.jobBoard;

export class FilterSearchParamMapper {
  #urlSearchParams: URLSearchParams;

  constructor(params: URLSearchParams | string) {
    this.#urlSearchParams = this.sanitizeParams(new URLSearchParams(params));
  }

  get urlSearchParams(): URLSearchParams {
    return this.#urlSearchParams;
  }

  /** Only the list state params, without LOCALE or any other param */
  get listSearchParams(): URLSearchParams {
    const listParams = new URLSearchParams();

    for (const key of Object.values(JOB_BOARD_QUERY_PARAMS)) {
      const value = this.#urlSearchParams.get(key);

      if (value) {
        listParams.set(key, value);
      }
    }

    listParams.sort();

    return listParams;
  }

  static hasListSearchParams(params: URLSearchParams): boolean {
    return Object.values(JOB_BOARD_QUERY_PARAMS).some((key) => params.has(key));
  }

  /**
   * Converts URLSearchParams to JobPostFilters
   * Returns a JobPostFilters instance.
   */
  toFilters(): JobPostFilters {
    const filters: JobPostFilters = {};

    const page = this.#urlSearchParams.get(JOB_BOARD_QUERY_PARAMS.page);

    if (page) {
      filters.page = Number(page);
    }

    const searchText = this.#urlSearchParams.get(JOB_BOARD_QUERY_PARAMS.searchText)?.trim();

    if (searchText) {
      filters.searchText = searchText;
    } else {
      filters.searchText = undefined;
    }

    const jobType = this.#urlSearchParams.get(JOB_BOARD_QUERY_PARAMS.jobType);

    if (jobType) {
      filters.locationType = jobType as LocationType;
    } else {
      filters.locationType = undefined;
    }

    const subdivisions = this.#urlSearchParams.get(JOB_BOARD_QUERY_PARAMS.location);

    if (subdivisions) {
      filters.subdivisionCodes = subdivisions.split(",");
    } else {
      filters.subdivisionCodes = undefined;
    }

    return filters;
  }

  /**
   * Converts JobPostFilters to URLSearchParams
   * Updates FilterSearchParamMapper instance.
   */
  applyFilter(filters: JobPostFilters) {
    const nextParams = new URLSearchParams(this.#urlSearchParams);

    nextParams.delete(JOB_BOARD_QUERY_PARAMS.searchText);
    nextParams.delete(JOB_BOARD_QUERY_PARAMS.page);
    nextParams.delete(JOB_BOARD_QUERY_PARAMS.jobType);
    nextParams.delete(JOB_BOARD_QUERY_PARAMS.location);

    const normalizedSearchText = filters.searchText?.trim();

    if (normalizedSearchText) {
      nextParams.set(JOB_BOARD_QUERY_PARAMS.searchText, normalizedSearchText);
    }

    if (filters.page) {
      nextParams.set(JOB_BOARD_QUERY_PARAMS.page, String(filters.page));
    }

    if (filters.locationType && AllLocationTypes.includes(filters.locationType as LocationType)) {
      nextParams.set(JOB_BOARD_QUERY_PARAMS.jobType, filters.locationType);
    }

    const uniqueSubdivisions = Array.from(
      new Set((filters.subdivisionCodes ?? []).map((c) => c.trim()).filter(Boolean))
    );

    if (uniqueSubdivisions.length > 0) {
      nextParams.set(JOB_BOARD_QUERY_PARAMS.location, uniqueSubdivisions.join(","));
    }

    this.#urlSearchParams = this.sanitizeParams(nextParams);
  }

  /**
   * Replaces the list state params with the ones saved in listState.
   * Other params such as LOCALE are kept, and invalid saved values are dropped.
   */
  applyListState(listState: string) {
    const nextParams = new URLSearchParams(this.#urlSearchParams);
    const listParams = new URLSearchParams(listState);

    for (const key of Object.values(JOB_BOARD_QUERY_PARAMS)) {
      nextParams.delete(key);

      const value = listParams.get(key);

      if (value) {
        nextParams.set(key, value);
      }
    }

    this.#urlSearchParams = this.sanitizeParams(nextParams);
  }

  setPage(newPage: number) {
    const page = Math.max(1, newPage);

    return this.applyFilter({
      ...this.toFilters(),
      page,
    });
  }

  /** Compare equality of FilterSearchParamMapper params */
  isFiltersEqual(that: FilterSearchParamMapper): boolean {
    return this.#urlSearchParams.toString() === that.#urlSearchParams.toString();
  }

  /** Whether that only clears the search, the job type, or some locations of these filters */
  isFilterRemoval(that: FilterSearchParamMapper): boolean {
    const filters = this.toFilters();
    const thatFilters = that.toFilters();

    return (
      (thatFilters.searchText === undefined || thatFilters.searchText === filters.searchText) &&
      (thatFilters.locationType === undefined ||
        thatFilters.locationType === filters.locationType) &&
      (thatFilters.subdivisionCodes ?? []).every((code) => filters.subdivisionCodes?.includes(code))
    );
  }

  hasActiveFilters(): boolean {
    const filters = this.toFilters();

    return (
      Boolean(filters.searchText?.trim()) ||
      Boolean(filters.locationType) ||
      (filters.subdivisionCodes?.length ?? 0) > 0
    );
  }

  private sanitizeParams(params: URLSearchParams): URLSearchParams {
    const nextParams = new URLSearchParams(params);

    nextParams.delete(JOB_BOARD_QUERY_PARAMS.searchText);
    nextParams.delete(JOB_BOARD_QUERY_PARAMS.page);
    nextParams.delete(JOB_BOARD_QUERY_PARAMS.jobType);
    nextParams.delete(JOB_BOARD_QUERY_PARAMS.location);

    const searchText = params.get(JOB_BOARD_QUERY_PARAMS.searchText)?.trim();

    if (searchText) {
      nextParams.set(JOB_BOARD_QUERY_PARAMS.searchText, searchText);
    }

    const pageParam = params.get(JOB_BOARD_QUERY_PARAMS.page);

    if (pageParam) {
      const pageNumber = Number(pageParam);

      if (Number.isInteger(pageNumber) && pageNumber >= 1) {
        nextParams.set(JOB_BOARD_QUERY_PARAMS.page, String(pageNumber));
      }
    }

    const jobTypeParam = params.get(JOB_BOARD_QUERY_PARAMS.jobType);

    if (jobTypeParam && AllLocationTypes.includes(jobTypeParam as LocationType)) {
      nextParams.set(JOB_BOARD_QUERY_PARAMS.jobType, jobTypeParam);
    }

    const locationParam = params.get(JOB_BOARD_QUERY_PARAMS.location);

    if (locationParam) {
      const uniqueSubdivisions = Array.from(
        new Set(
          locationParam
            .split(",")
            .map((c) => c.trim().toUpperCase())
            .filter(Boolean)
            .map((c) => (c.startsWith("CA-") ? c : `CA-${c}`))
            .filter((c) => AllProvinces.includes(c as Province))
        )
      );

      if (uniqueSubdivisions.length > 0) {
        nextParams.set(JOB_BOARD_QUERY_PARAMS.location, uniqueSubdivisions.join(","));
      }
    }

    nextParams.sort();

    return nextParams;
  }

  clone() {
    return new FilterSearchParamMapper(this.#urlSearchParams);
  }
}
